import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../store";
import {requestSubscribedBiasUsers, updateSubscribedBiasUsersDB, deleteSubscribedBiasUsers, requestMaxSubscriptions} from "../api/api-subscribed-bias";
import UserSubscribedBias from "../datatypes/UserSubscribedBias";


export interface SubscribedUserState {
    data: typeof UserSubscribedBias[],
    loading: boolean,
    error: any, 
    subscriptionCount: number, 
    maxSubscriptions: number,

}

const initialState: SubscribedUserState = {
    data: [],
    loading: false,
    error: null,
    subscriptionCount: 0, 
    maxSubscriptions: 0,
}

export const userSubscribedBiasThunk = createAsyncThunk(
    'subscribedBias/getSubs',
    async ({uid, symbol, traderType}: { uid: number, symbol?: string, traderType?: string }) => {
        const response = await requestSubscribedBiasUsers(uid, symbol, traderType);
        return response?.data;
    }
)

export const updateSubscribedBiasUsersDBThunk = createAsyncThunk(
    'subscribedBias/updateSubs',
    async ({uid, symbol, traderType, discordUsername, dropDownSelectedBiasList}: { uid: number, symbol: string, traderType: string, discordUsername: string, dropDownSelectedBiasList: number[] }) => {
        const response = await updateSubscribedBiasUsersDB(uid, symbol, traderType, discordUsername, dropDownSelectedBiasList);
        return response?.data;
    }
)

export const deleteSubscribedBiasThunk = createAsyncThunk(
    'subscribedBias/deleteAllSubs',
    async ({uid}: { uid: number}) => {
        const response = await deleteSubscribedBiasUsers(uid);
        return response?.data;
    }
)

export const getMaxSubscriptionsThunk = createAsyncThunk(
    'subscribedBias/getMaxSubscriptions',
    async ({level}: {level: number}) => {
        const response = await requestMaxSubscriptions(level);
        return response?.data;
    }
)


export const userSubscribedBiasReducer = createSlice({
    name: 'main',
    initialState,
    reducers: {
        updateSubscribedUserData: (state: SubscribedUserState, action: PayloadAction<typeof UserSubscribedBias[]>) => {
            state.data = action.payload;
        },
        updateSubscribedProgress: (state: SubscribedUserState, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Handles the pending action of the fetchUserData async thunk
            .addCase(userSubscribedBiasThunk.pending, (state) => {
                state.loading = true;
            })
            // Handles the fulfilled action of the fetchUserData async thunk
            .addCase(userSubscribedBiasThunk.fulfilled, (state, action) => {
                state.loading = false;
                if ('data' in action.payload) {
                    state.data = action.payload.data;
                    state.subscriptionCount = state.data.reduce((a, b) => a + b.bias_list.length, 0);
                }
            })
            // Handles the rejected action of the fetchUserData async thunk
            .addCase(userSubscribedBiasThunk.rejected, (state, action) => {
                state.loading = false;
                state.data = []
                state.error = action.error.message;
            })
            .addCase(updateSubscribedBiasUsersDBThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateSubscribedBiasUsersDBThunk.fulfilled, (state, action) => {
                const args = action.meta.arg;
                
                const dataCopy = state.data;
                const filterDataForEntry = dataCopy.filter(el => el.uid === args.uid && el.symbol === args.symbol && el.trader_type === args.traderType);
                if (filterDataForEntry.length === 0) {
                    dataCopy.push({uid: args.uid, symbol: args.symbol, trader_type: args.traderType, discord_name: args.discordUsername, bias_list: args.dropDownSelectedBiasList});
                } else {
                    const entry = filterDataForEntry[0]; 
                    const idx = dataCopy.indexOf(entry);
                    if (args.dropDownSelectedBiasList.length === 0) {
                        dataCopy.splice(idx, 1);
                    } else {
                        dataCopy[idx].bias_list = args.dropDownSelectedBiasList;
                    }
                }
                state.data = dataCopy;
                state.subscriptionCount = state.data.reduce((a, b) => a + b.bias_list.length, 0);
                state.loading = false;
            })
            .addCase(updateSubscribedBiasUsersDBThunk.rejected, (state, action) => {
                state.loading = false; 
                state.error = action.error.message;
            })
            .addCase(deleteSubscribedBiasThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteSubscribedBiasThunk.fulfilled, (state) => {
                state.loading = false;
                state.data = [];
                state.subscriptionCount = 0;
            })
            .addCase(deleteSubscribedBiasThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(getMaxSubscriptionsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getMaxSubscriptionsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.maxSubscriptions = action.payload.data;
            })
            .addCase(getMaxSubscriptionsThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
    },
})

export const {
    updateSubscribedUserData, updateSubscribedProgress
} = userSubscribedBiasReducer.actions;

export const selectSubscribedUserData = (state: RootState) => state.userSubscribedBiasData.data;
export const selectSubscribedProgress = (state: RootState) => state.userSubscribedBiasData.loading;
export const selectSubscriptionCount= (state: RootState) => state.userSubscribedBiasData.subscriptionCount;
export const selectMaxSubscriptionCount = (state: RootState) => state.userSubscribedBiasData.maxSubscriptions;

export default userSubscribedBiasReducer.reducer;